import { Injectable } from '@angular/core';
import moment from 'moment';

/**
 * Service for calculating week numbers and years using studio-specific logic.
 *
 * This service implements week calculation with Sunday as the first day of the week,
 * and handles special cases at year boundaries including years with 53 weeks.
 *
 * Week Rules:
 * - Weeks run Sunday-Saturday
 * - Week 1 ends on the first Saturday of January
 * - "4+ days" rule: If 4 or more days of what would be Week 1 of next year
 *   fall in the current year, those days become Week 53 of the current year
 * - When a year has Week 53, the following year's Week 1 starts the day AFTER
 *   the first Saturday of January
 */
@Injectable({
  providedIn: 'root'
})
export class WeekCalculationService {

  constructor() {
    // Configure moment to use Sunday as first day of week
    moment.updateLocale('en', {
      week: {
        dow: 0, // Sunday as the first DOW.
      }
    });
  }

  /**
   * Gets the first Saturday of January for a given year
   */
  private getFirstSaturdayOfJanuary(year: number): Date {
    const firstDayOfYear = new Date(year, 0, 1);
    const dayOfWeek = firstDayOfYear.getDay();
    const daysToFirstSaturday = (6 - dayOfWeek + 7) % 7;
    return new Date(year, 0, 1 + daysToFirstSaturday);
  }

  /**
   * Determines the maximum number of weeks in a year using the "4+ days" rule.
   *
   * If 4 or more days of what would be the naive "Week 1" of the next year
   * fall in the current year, then the current year has 53 weeks.
   */
  getMaxWeeksInYear(year: number): number {
    const nextYear = year + 1;
    const firstSatNextYear = this.getFirstSaturdayOfJanuary(nextYear);

    // The naive Week 1 of next year would start 6 days before first Saturday
    const week1StartNextYear = new Date(firstSatNextYear);
    week1StartNextYear.setDate(week1StartNextYear.getDate() - 6);

    // Count how many days of that week fall in the current year
    const jan1NextYear = new Date(nextYear, 0, 1);
    const daysInCurrentYear = Math.floor((jan1NextYear.getTime() - week1StartNextYear.getTime()) / (24 * 60 * 60 * 1000));

    return daysInCurrentYear >= 4 ? 53 : 52;
  }

  /**
   * Gets the start date (Sunday) of Week 1 for a given year.
   * Accounts for whether the previous year had Week 53.
   */
  private getWeek1Start(year: number): Date {
    const firstSaturdayOfJanuary = this.getFirstSaturdayOfJanuary(year);
    const prevYearHasWeek53 = this.getMaxWeeksInYear(year - 1) === 53;

    if (prevYearHasWeek53) {
      // If previous year had Week 53, Week 1 starts the day AFTER first Saturday
      const week1Start = new Date(firstSaturdayOfJanuary);
      week1Start.setDate(week1Start.getDate() + 1);
      return week1Start;
    } else {
      // Normal case: Week 1 starts 6 days before first Saturday
      const week1Start = new Date(firstSaturdayOfJanuary);
      week1Start.setDate(week1Start.getDate() - 6);
      return week1Start;
    }
  }

  /**
   * Calculate week number and year from a date string
   *
   * @param dateStr - ISO date string to calculate week info for
   * @returns Object containing weekNumber and year
   */
  calculateWeekInfo(dateStr: string): { weekNumber: number, year: number } {
    const year = this.getCorrectYearForWeek(dateStr);
    const weekNumber = this.getWeekNumber(dateStr);

    return { weekNumber, year };
  }

  /**
   * Gets the week number for a date using custom week calculation logic.
   *
   * @param dateStr - ISO date string to get the week number for
   * @returns The week number (1-53)
   */
  getWeekNumber(dateStr: string): number {
    const date = new Date(dateStr);
    const calendarYear = date.getFullYear();

    // Get Week 1 start for this calendar year
    const week1Start = this.getWeek1Start(calendarYear);

    // If date is before Week 1 starts, it's in the last week of previous year
    if (date < week1Start) {
      const prevYearWeek1Start = this.getWeek1Start(calendarYear - 1);
      const daysDiff = Math.floor((date.getTime() - prevYearWeek1Start.getTime()) / (24 * 60 * 60 * 1000));
      return Math.floor(daysDiff / 7) + 1;
    }

    // Calculate week number from Week 1 start
    const daysDiff = Math.floor((date.getTime() - week1Start.getTime()) / (24 * 60 * 60 * 1000));
    const weekNumber = Math.floor(daysDiff / 7) + 1;

    // Check if we're past the max weeks for this year (belongs to next year's Week 1)
    const maxWeeks = this.getMaxWeeksInYear(calendarYear);
    if (weekNumber > maxWeeks) {
      return 1;
    }

    return weekNumber;
  }

  /**
   * Determines the correct year to use for week-based calculations, especially at year boundaries.
   *
   * This handles cases where:
   * - Late December dates might be Week 1 of the next year (when prev year has no Week 53)
   * - Late December dates might be Week 53 of the current year (when current year has Week 53)
   * - Early January dates might be Week 52/53 of the previous year
   *
   * @param dateStr - ISO date string to determine the correct year for
   * @returns The correct year to use for week-based calculations
   */
  getCorrectYearForWeek(dateStr: string): number {
    const date = new Date(dateStr);
    const calendarYear = date.getFullYear();

    // Get Week 1 start for this calendar year
    const week1Start = this.getWeek1Start(calendarYear);

    // If date is before Week 1 starts, it belongs to previous year
    if (date < week1Start) {
      return calendarYear - 1;
    }

    // Calculate week number
    const daysDiff = Math.floor((date.getTime() - week1Start.getTime()) / (24 * 60 * 60 * 1000));
    const weekNumber = Math.floor(daysDiff / 7) + 1;

    // Check if we're past the max weeks for this year
    const maxWeeks = this.getMaxWeeksInYear(calendarYear);
    if (weekNumber > maxWeeks) {
      return calendarYear + 1;
    }

    return calendarYear;
  }
}

